import React, { useCallback, useEffect, useState } from "react";
import { Footer, TutorialDisplay, Section, Popover } from "../components";
import { Chapter, ChapterBeat, Choice, ContentType } from "../story";
import { UiProvider } from "../contexts";

/**
 * Parameters expected by a GamePage.
 * @param {Chapter} chapterContent  - The Chapter object, containing all ChapterBeat objects for the given chapter.
 * @param {function} chapterEnded - An optional function to call when the chapter ends.
 * @param {boolean} isLastChapter - When true, the GamePage will stop adding content to the display.
 */
interface GamePageProps {
    currentChapter: Chapter;
    onChapterEnded: (storyBeat: number) => void;
    isLastChapter: boolean;
    numberOfBeats: number;
    onResetStory: () => void;
}

/**
 * The main component for displaying game assets according to the contents and type of a Chapter object. 
 * @param {GamePageProps} props - Parameters expected by the GamePage.
 * */
export const GamePage = (props: GamePageProps) => {
    const { currentChapter, onChapterEnded, isLastChapter, numberOfBeats, onResetStory } = props;
    const { chapterBeats } = currentChapter;
    const { index : chapterIndex } = currentChapter;
    const initialStoryText = [] as ChapterBeat[]
    const [storyText, setStoryText] = useState(initialStoryText);
    const [isProgressDisabled, setProgressDisabled] = useState(false);
    const [loadedBeatCount, setLoadedBeatCount] = useState(0);
    const initialTutorialState = 0;
    const [isTutorialActive, setTutorialActive] = useState(false);
    const [tutorialState, setTutorialState] = useState(initialTutorialState);
    const initialBeatState = -1;
    const [storyBeat, setStoryBeat] = useState(initialBeatState);
    const placeholderElementId = "bottom";
    const [showChoiceTip, setChoiceTip] = useState(false);
    const [seenChoiceTip, setSeenChoiceTip] = useState(false);

    const startScrollAdjustmentForConsequence = useCallback((lastBeatIndex: number, selectedElement: HTMLElement | null) => {
        if (selectedElement === null) {
            return;
        }
        const consequenceContentId = `${storyText[lastBeatIndex].beatId}-end`;
        const consequenceElement = document.getElementById(consequenceContentId);
        consequenceElement!!.onload = () => selectedElement?.scrollIntoView({behavior: "smooth", inline:"center", block:"center"});
    }, [storyText]);

    const startScrollBehavior = useCallback(() => {
        const lastBeatIndex = storyText.length-1;
        const displayIsEmpty = storyText[lastBeatIndex] === undefined;
        if (displayIsEmpty) {
            return;
        }
        const currentContentId =  storyText[lastBeatIndex].beatId;
        const selectedElement = document.getElementById(currentContentId);
        if (selectedElement) selectedElement.onload = () => selectedElement?.scrollIntoView({behavior: "smooth", inline:"center", block:"center"});
        const isConsequence = storyText[lastBeatIndex].contentType === ContentType.CONSEQUENCE;
        if (isConsequence) {
            startScrollAdjustmentForConsequence(lastBeatIndex, selectedElement);
        }
      }, [storyText, startScrollAdjustmentForConsequence]);

    const progressStory = (choice?: Choice) => {       
        if (choice?.consequenceText !== undefined) {
            addConsequenceBeat(choice);
            setProgressDisabled(false);
            setTutorialState(prevState => prevState + 1);
            setChoiceTip(false);
            return;
        }

        switch (storyBeat) {
            case initialBeatState:
                startChapter();
                break;
            case chapterBeats.length:
                endChapter();
                break;
            default:
                addNextBeat();
        }

        const lastTutorialStep = 3;
        setTutorialState(prevState => prevState + 1);
        setTutorialActive(tutorialState <= lastTutorialStep)
    }

    const addNextBeat = () => {
        const currentBeat = chapterBeats[storyBeat];
        const hasChoiceMarker = currentBeat.contentType === ContentType.CHOICE;
        setProgressDisabled(hasChoiceMarker);
        setStoryText(prevBeats => [...prevBeats, currentBeat]);
        setStoryBeat(prevBeat => prevBeat + 1);
        incrementBeatCount();
        document.getElementById("bottom")?.scrollIntoView(true);
        determineTipStatus(hasChoiceMarker)
    }

    const determineTipStatus = (hasChoiceMarker: boolean) => {
        if (seenChoiceTip) {
            return;
        }
        const firstChapterIndex = 1;
        const shouldShowTip = hasChoiceMarker && !isTutorialActive && currentChapter.index === firstChapterIndex;
        setChoiceTip(shouldShowTip);
        setSeenChoiceTip(shouldShowTip);
    }

    const createChapterTitle = useCallback((title: string, subtitle: string) => {
        const titleChapterIndex = -1;
        const lastChapterIndex = 5;
        const isTitleofStoryStart = currentChapter.index === titleChapterIndex;
        const isTitleOfLastChapter = currentChapter.index === lastChapterIndex;
        return (isTitleofStoryStart || isTitleOfLastChapter) ? new ChapterBeat(title, ContentType.TITLE, "logo.png", subtitle) : new ChapterBeat(title, ContentType.TITLE);
    },[currentChapter.index]);

    const startChapter = useCallback(() => {
        currentChapter.title = currentChapter.title.toUpperCase()
        setProgressDisabled(isLastChapter);
        if(isLastChapter) return;
        const openingBeat = createChapterTitle(currentChapter.title, currentChapter.subtitle ?? "");
        setProgressDisabled(false);
        setStoryText(prevBeats => [...prevBeats, openingBeat]);
        setStoryBeat(0);
    },[createChapterTitle, currentChapter, isLastChapter]);

    const endChapter = () => {
        setProgressDisabled(isLastChapter);
        setStoryBeat(initialBeatState);
        onChapterEnded(storyBeat);
    }
    
    const addConsequenceBeat = (choice: Choice) => {
        const currentBeat = new ChapterBeat(choice, ContentType.CONSEQUENCE);
        setStoryText(prevBeats => [...prevBeats, currentBeat]);
    }

    const incrementBeatCount = () => {
        setLoadedBeatCount(prevCount => prevCount+1);
    }

    const resetStoryBeats = () => {
        setStoryText(initialStoryText);
        setStoryBeat(initialBeatState);
        setLoadedBeatCount(0);
        setProgressDisabled(false);
    }

    const ChoiceTip = () => {
        return (
            <>
            <Popover 
            content={"Click a choice above or click the arrows to select a choice. Click the selected choice again or the center arrow to confirm."} 
            arrowPosition={"none"}
            onPopoverClicked={() => setChoiceTip(false)}/>;  
            </>
        );
    }

    const updateTutorialState = () => {
        setChoiceTip(false);
        setTutorialState(initialTutorialState);
    }
          
    const resetToTitle = useCallback(() => {
        console.log("DEBUG","RESETTOTITLE")
        setSeenChoiceTip(false);
        setChoiceTip(false);
        startChapter();
    },[startChapter])

    useEffect(() => {
        const lastBeatIndex = storyText.length-1;
        const displayIsEmpty = storyText[lastBeatIndex] === undefined;
        if (displayIsEmpty) {
            resetToTitle();
        }
        startScrollBehavior();
    },
      [storyText, startScrollBehavior, resetToTitle]
    );

    return (
        <div>
            <div className="game-wrapper">
                <div className="top-scroll-div"><p>.</p></div>
                {storyText.map((item) => {
                    return (
                        <Section
                        key={item.beatId} 
                        scrollId={item.beatId}
                        onChoiceSelected={progressStory} 
                        storyBeat={item}/>
                    );
                })}
                <div className="bottom-scroll-div" id={placeholderElementId}><p>.</p></div>
            </div>
            <UiProvider>
                <TutorialDisplay 
                tutorialState={tutorialState} 
                isProgressDisabled={isProgressDisabled}
                onPopoverClicked={() => setTutorialState(prevState => prevState + 1)}/>
                <Footer 
                isProgressDisabled={isProgressDisabled} 
                onProgressClicked={progressStory} 
                onHelpPressed={() => updateTutorialState()} 
                currentProgress={loadedBeatCount} 
                maxProgress={numberOfBeats}
                currentChapterIndex={chapterIndex}
                onRestartGame={onResetStory}
                onResetBeats={resetStoryBeats}/>
                {(showChoiceTip) && <ChoiceTip/>}
            </UiProvider>
        </div>
    );
}